import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Config from '../config/Config.js';
import { RESOLVE_LOCAL_AUTH_STATE, RESOLVE_LOCAL_USER_STATE } from '../components/auth/AuthActions';
import SweetLoadingSpinner from '../components/eyeCandy/SweetLoadingSpinner.js';

const API_BASE = Config().url;

const MetricsPage = () => {
    const auth = RESOLVE_LOCAL_AUTH_STATE();
    const [data, setData] = useState(null);
    const [days, setDays] = useState(7);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const user = RESOLVE_LOCAL_USER_STATE();
    const isOwner = auth.is_authenticated === 'true' && user.pk === '1';

    useEffect(() => {
        if (!isOwner) {
            setError('You don\u2019t have permission to view this page.');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        axios
            .get(`${API_BASE}api/v1/metrics/?days=${days}`, {
                headers: { Authorization: `Token ${auth.token}` },
            })
            .then((res) => {
                setData(res.data);
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message || 'Failed to load metrics.');
                setLoading(false);
            });
    }, [days, auth.token, isOwner]);

    if (error) {
        return (
            <div className="container" style={{ paddingTop: 50 }}>
                <p style={{ color: '#991b1b' }}>{error}</p>
            </div>
        );
    }

    return (
        <div className="container" style={{ paddingTop: 50, paddingBottom: 60 }}>
            <h4 style={{ marginBottom: 24, color: 'var(--ink)' }}>Platform Metrics</h4>

            {loading && <SweetLoadingSpinner loadingState={true} />}

            {data && (
                <>
                    {/* ===== SECTION 1: ALL-TIME TOTALS ===== */}
                    <h5 style={{ color: 'var(--ink)', marginBottom: 20 }}>All-Time Totals</h5>

                    <div className="row" style={{ marginBottom: 30 }}>
                        <StatCard label="Total Users" value={data.platform.total_users} />
                        <StatCard label="Total Projects" value={data.platform.total_projects} />
                        <StatCard label="Published" value={data.platform.published_projects} />
                        <StatCard label="Unpublished" value={data.platform.unpublished_projects} />
                    </div>

                    <Section title="Projects By Owner">
                        <SimpleBarChart
                            items={data.platform.projects_by_owner.map((o) => ({
                                label: o.owner__username,
                                value: o.count,
                            }))}
                        />
                    </Section>

                    <Section title="Projects By Technology">
                        <SimpleBarChart
                            items={data.platform.projects_by_technology.map((t) => ({
                                label: t.technology,
                                value: t.count,
                            }))}
                        />
                    </Section>

                    <Section title="Projects By Skill">
                        <SimpleBarChart
                            items={data.platform.projects_by_skill.map((s) => ({
                                label: s.skill,
                                value: s.count,
                            }))}
                        />
                    </Section>

                    {/* ===== SECTION 2: TIME-SLICED REQUEST DATA ===== */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 40, marginBottom: 20 }}>
                        <h5 style={{ margin: 0, color: 'var(--ink)' }}>Request Activity</h5>
                        <div>
                            {[7, 14, 30, 90].map((d) => (
                                <button
                                    key={d}
                                    onClick={() => setDays(d)}
                                    style={{
                                        padding: '6px 14px',
                                        marginLeft: 6,
                                        border: 'none',
                                        borderRadius: 4,
                                        cursor: 'pointer',
                                        fontFamily: 'Courier',
                                        fontSize: '0.85rem',
                                        backgroundColor: days === d ? 'var(--brand, #59c6c1)' : 'var(--bg-soft, #f5f5f5)',
                                        color: days === d ? '#fff' : 'var(--ink, #333)',
                                    }}
                                >
                                    {d}d
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="row" style={{ marginBottom: 30 }}>
                        <StatCard label={`Requests (${days}d)`} value={data.requests.total} />
                        <StatCard label="Avg Response" value={data.requests.avg_response_ms !== null ? `${Math.round(data.requests.avg_response_ms)}ms` : 'N/A'} />
                        <StatCard
                            label="Auth / Anon"
                            value={`${data.users.authenticated_vs_anonymous.authenticated} / ${data.users.authenticated_vs_anonymous.anonymous}`}
                        />
                        <StatCard label="Period" value={`${days} days`} />
                    </div>

                    <Section title="Requests Per Day">
                        <SimpleBarChart
                            items={data.requests.per_day.map((r) => ({
                                label: r.date,
                                value: r.count,
                                sub: r.avg_response_ms !== null ? `${Math.round(r.avg_response_ms)}ms avg` : '',
                            }))}
                        />
                    </Section>

                    <Section title="Status Codes">
                        <SimpleBarChart
                            items={data.requests.status_codes.map((s) => ({
                                label: String(s.status_code),
                                value: s.count,
                            }))}
                        />
                    </Section>

                    <Section title="By Method">
                        <SimpleBarChart
                            items={data.requests.by_method.map((m) => ({
                                label: m.method,
                                value: m.count,
                            }))}
                        />
                    </Section>

                    <Section title="Top Endpoints">
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Path</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Hits</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Avg ms</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.requests.top_endpoints.map((e, i) => (
                                    <tr key={i}>
                                        <td style={tdStyle}>{e.path}</td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>{e.count}</td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>{e.avg_response_ms !== null ? Math.round(e.avg_response_ms) : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Section>

                    <Section title="Top Active Users">
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>User</th>
                                    <th style={thStyle}>Email</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Requests</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.users.top_active.map((u, i) => (
                                    <tr key={i}>
                                        <td style={tdStyle}>{u.user__username}</td>
                                        <td style={tdStyle}>{u.user__email}</td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>{u.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Section>

                    <Section title="Top IP Addresses">
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>IP Address</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Requests</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.top_ips.map((ip, i) => (
                                    <tr key={i}>
                                        <td style={tdStyle}>{ip.ip_address || 'unknown'}</td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>{ip.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Section>
                </>
            )}
        </div>
    );
};

/* ---------- tiny reusable pieces ---------- */

const StatCard = ({ label, value }) => (
    <div className="col-md-3 col-sm-6" style={{ marginBottom: 12 }}>
        <div
            style={{
                background: 'var(--bg-soft, #f8f8f8)',
                borderRadius: 8,
                padding: '16px 20px',
                textAlign: 'center',
            }}
        >
            <div style={{ fontSize: '1.6rem', fontWeight: 700, fontFamily: 'Courier', color: 'var(--ink, #222)' }}>{value}</div>
            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 4 }}>{label}</div>
        </div>
    </div>
);

const Section = ({ title, children }) => (
    <div style={{ marginBottom: 30 }}>
        <h6 style={{ color: 'var(--ink)', backgroundColor: 'var(--bg-soft)', padding: 5, display: 'inline-block' }}>{title}</h6>
        <hr style={{ marginTop: 4 }} />
        {children}
    </div>
);

const SimpleBarChart = ({ items }) => {
    if (!items || items.length === 0) return <p style={{ color: '#888' }}>No data</p>;
    const max = Math.max(...items.map((i) => i.value), 1);
    return (
        <div>
            {items.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ width: 140, fontSize: '0.8rem', fontFamily: 'Courier', color: 'var(--ink, #333)', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.label}
                    </div>
                    <div style={{ flex: 1, marginLeft: 8, marginRight: 8 }}>
                        <div
                            style={{
                                height: 18,
                                width: `${(item.value / max) * 100}%`,
                                minWidth: 2,
                                backgroundColor: 'var(--brand, #59c6c1)',
                                borderRadius: 3,
                            }}
                        />
                    </div>
                    <div style={{ fontSize: '0.8rem', fontFamily: 'Courier', fontWeight: 600, minWidth: 40, textAlign: 'right' }}>
                        {item.value}
                    </div>
                    {item.sub && <div style={{ fontSize: '0.7rem', color: '#999', marginLeft: 6, minWidth: 60 }}>{item.sub}</div>}
                </div>
            ))}
        </div>
    );
};

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', fontFamily: 'Courier' };
const thStyle = { borderBottom: '2px solid #ddd', padding: '6px 10px', textAlign: 'left', color: '#888', fontWeight: 600 };
const tdStyle = { borderBottom: '1px solid #eee', padding: '6px 10px', color: 'var(--ink, #333)' };

export default MetricsPage;
